import { Box } from "@vp/swan";
import React, { useEffect, useMemo } from "react";
import usePricingContextInitialization from "../../hooks/usePricingContextInitialization";
import { useStablePriceDetails } from "../../hooks/useStablePriceDetails";
import { productPricingService } from "../../services/productPricingService";
import type { PriceProperties } from "../../types/pricing/price-display.types";
import { PriceDisplayType } from "../../types/pricing/price-display.types";
import { logger } from "../../util/logger";
import { isRangePriceDisplayGroup } from "../../util/price-calculator-helper";
import { calculateResolvedPrices } from "../../util/price-extraction-helper";
import { getPriceQueryId } from "../../util/price-queryId";
import PriceLoading from "../PriceLoading";
import { PriceDisplayV2Props } from "./PriceDisplayV2";
import { PriceDisplayWrapper } from "./PriceDisplayWrapper";
import { LOGO_CHARGE_TENANTS } from "../../constants/global";

// LogoProcessingCharge is client-side only — never a valid API display strategy.
const VALID_PRICE_DISPLAY_TYPES = new Set<string>(
  Object.values(PriceDisplayType).filter((v) => v !== PriceDisplayType.LogoProcessingCharge),
);

//Acts as a safeguard to not honor any display strategy present in PriceDisplay but not in PriceDisplay V2
const toValidDisplayStrategy = (value: string | undefined): PriceDisplayType | undefined => {
  if (value && VALID_PRICE_DISPLAY_TYPES.has(value)) {
    return value as PriceDisplayType;
  }
  return undefined;
};

export type PriceExtractionWrapperProps = PriceDisplayV2Props;

const PriceExtractionWrapper = ({
  mpvId,
  productId,
  version,
  selections,
  alternateSelections,
  requestedPriceDisplayStrategy,
  hideMinimumOrderableQuantity = false,
  displayVariant,
  alwaysShowMOQInfo = false,
  hidePriceDetails = false,
  alignVAT,
  isTile = false,
  developmentMode,
  docRefUrl,
  assumeDocRequiresCertification,
  showLogoChargeOnly,
}: PriceExtractionWrapperProps) => {
  const { pricingContextInitialized, vatIncl, pricingContextValue } = usePricingContextInitialization(
    productPricingService.tenant,
    productPricingService.locale,
    developmentMode,
  );
  const quantity = selections?.quantity ?? selections?.Quantity;

  const priceQueryId = getPriceQueryId({
    mpvId,
    productId,
    version,
    selections,
    alternateSelections,
    displayVariant,
    requestedPriceDisplayStrategy,
    docRefUrl,
    assumeDocRequiresCertification,
  });

  useEffect(() => {
    if (!pricingContextInitialized || !productPricingService.locale) return;
    if (showLogoChargeOnly && !docRefUrl && !assumeDocRequiresCertification) return;
    // Logo Processing Charge is only available for select tenants — skip the API call for others.
    if ((showLogoChargeOnly || docRefUrl || assumeDocRequiresCertification) && !LOGO_CHARGE_TENANTS.has(productPricingService.tenant)) return;

    productPricingService.queuePriceQuery({
      mpvId,
      productId,
      version,
      selections,
      alternateSelections,
      displayVariant,
      requestedPriceDisplayStrategy: toValidDisplayStrategy(requestedPriceDisplayStrategy),
      docRefUrl,
      assumeDocRequiresCertification,
    });
  }, [
    mpvId,
    productId,
    version,
    selections,
    alternateSelections,
    displayVariant,
    requestedPriceDisplayStrategy,
    pricingContextInitialized,
    pricingContextValue,
    productPricingService.tenant,
    productPricingService.locale,
    docRefUrl,
    assumeDocRequiresCertification,
  ]);

  const { priceDetails: priceDetailsToUse, evaluatedQuantity, isCurrentError } = useStablePriceDetails({
    priceQueryId,
    quantity,
    selections,
  });

  const hasValidPriceData = priceDetailsToUse?.prices?.estimatedPrices;

  // Compute resolved prices based on display strategy and evaluated quantity
  const resolvedPrices = useMemo(() => {
    if (!hasValidPriceData) return null;
    return calculateResolvedPrices(priceDetailsToUse, evaluatedQuantity);
  }, [hasValidPriceData, priceDetailsToUse, evaluatedQuantity]);

  const isError = productPricingService.isError || priceDetailsToUse?.isError || isCurrentError;
  const isMissingResolvedPrices = !resolvedPrices && !isError && priceDetailsToUse;

  useEffect(() => {
    if (isError || isMissingResolvedPrices) {
      logger.warn("Price details are unavailable", {
        productId,
        version,
        mpvId,
        selections,
        quantity,
        priceDetailsToUse,
        isError,
        isMissingResolvedPrices,
      });
    }
  }, [isError, isMissingResolvedPrices]);

  // showLogoChargeOnly with no doc context: the API call was already skipped above.
  // Return nothing rather than showing the loading spinner forever.
  if (showLogoChargeOnly && !docRefUrl && !assumeDocRequiresCertification) return <></>;

  // Logo Processing Charge is only available for select tenants — render nothing
  // immediately without showing a loading state (the API call was already skipped above).
  if ((showLogoChargeOnly || docRefUrl || assumeDocRequiresCertification) && !LOGO_CHARGE_TENANTS.has(productPricingService.tenant)) return <></>;

  if (isError || isMissingResolvedPrices) {
    return <></>;
  }

  // Render PriceDisplayWrapper when we have valid price data
  if (hasValidPriceData && resolvedPrices) {
    const priceProperties: PriceProperties = {
      minimumFractionDigits: priceDetailsToUse.prices?.fractionDigits ?? 2,
      currency: priceDetailsToUse.prices?.currency ?? "EUR",
      quantity: Number(evaluatedQuantity),
      vatInc: vatIncl,
      hideSetupFee: !isRangePriceDisplayGroup(priceDetailsToUse?.priceDisplayGroup),
      doesOfferExist: priceDetailsToUse.prices?.estimatedPrices[Number(evaluatedQuantity)]?.discountMetaData?.doesOfferExist,
      alignVAT,
      isTile: isTile,
      hideMinimumOrderableQuantity,
      alwaysShowMOQInfo,
      hidePriceDetails,
    };

    // Logo charge mode: triggered by showLogoChargeOnly or when doc context props are present.
    // Renders only the Logo Processing Charge (Art Validation Fee) as an additive "+price" amount
    // via DifferentialPrice, or nothing if the charge is absent or zero.
    if (showLogoChargeOnly || docRefUrl || assumeDocRequiresCertification) {
      const { logoProcessingChargeListPrice, logoProcessingChargeDiscountedPrice } = resolvedPrices;
      const logoChargeAmount = vatIncl
        ? logoProcessingChargeListPrice?.taxed
        : logoProcessingChargeListPrice?.untaxed;
      if (!logoChargeAmount) return <></>;

      return (
        <Box aria-live="polite" aria-atomic="true" data-testid="price-extraction-wrapper-test-id">
          <PriceDisplayWrapper
            priceDisplayType={PriceDisplayType.LogoProcessingCharge}
            prices={{
              ...resolvedPrices,
              differentialListPrice: logoProcessingChargeListPrice,
              differentialDiscountedPrice: logoProcessingChargeDiscountedPrice ?? logoProcessingChargeListPrice,
            }}
            priceProperties={priceProperties}
          />
        </Box>
      );
    }

    return (
      <Box aria-live="polite" aria-atomic="true" data-testid="price-extraction-wrapper-test-id">
        <PriceDisplayWrapper
          priceDisplayType={priceDetailsToUse.displayStrategy}
          prices={resolvedPrices}
          priceProperties={priceProperties}
        />
      </Box>
    );
  }

  return (
    <PriceLoading />
  );
};

export { PriceExtractionWrapper };
